import { Ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  PropertyType,
  AwayModeFormType,
  PropertyVacationType
} from '@/interfaces'
import { useRouter } from 'vue-router'
import { get, put, post, del } from './helpers'

interface UsePropertiesQueryResponse {
  isPending: Ref<boolean>
  isSuccess: Ref<boolean>
  isError: Ref<boolean>
  data: Ref<PropertyType[] | undefined>
  error: Ref<Error | null>
  refetch: () => void
}

const REFETCH_INTERVAL_15_MIN = 1000 * 60 * 15

export const usePropertiesQuery = (options?: {
  enabled?: boolean | Ref<boolean>
}): UsePropertiesQueryResponse => {
  const router = useRouter()
  const enabled = options?.enabled ?? true
  const query = useQuery({
    queryKey: ['properties'],
    queryFn: async () => get(`/properties`, router),
    refetchInterval: REFETCH_INTERVAL_15_MIN,
    refetchIntervalInBackground: false,
    enabled,
    select: (data: PropertyType[]) => {
      return data
    }
  })

  return query
}

interface UsePropertiesPutMutationResponse {
  message: string
}

interface UsePropertiesPutMutationProps {
  propertyId: Ref<number>
  onSuccessCallback?: (response: UsePropertiesPutMutationResponse) => void
  onErrorCallback?: (error: unknown) => void
}

interface UsePropertiesPutMutationType {
  isPending: Ref<boolean>
  isSuccess: Ref<boolean>
  isError: Ref<boolean>
  error: Ref<Error | null>
  data: Ref<UsePropertiesPutMutationResponse | undefined>
  savePropertyChanges: (payload: PutPayload) => void
}

interface PropertyLabelPayload {
  property: {
    label: string
  }
}

interface PropertyHotWaterPayload {
  property: {
    battery_saver_enabled: boolean
    load_shedding_enabled: boolean
  }
}

interface PropertyLoadsheddingPayload {
  property: {
    esp_area_id: number | null
  }
}

export type PutPayload =
  PropertyLabelPayload | PropertyHotWaterPayload | PropertyLoadsheddingPayload

export const usePropertiesPutMutation = ({
  propertyId,
  onSuccessCallback,
  onErrorCallback
}: UsePropertiesPutMutationProps): UsePropertiesPutMutationType => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const mutation = useMutation<
    UsePropertiesPutMutationResponse,
    Error,
    PutPayload
  >({
    mutationFn: (body) =>
      put(
        `/properties/${propertyId.value}`,
        body,
        router,
        propertyId.value.toString()
      ),
    onSuccess: (response, variables) => {
      if ('label' in variables.property) {
        const label = variables.property.label
        if (typeof label !== 'string') return
        queryClient.setQueryData<PropertyType[]>(['properties'], (old) => {
          if (!old) return old
          const id = propertyId.value
          return old.map((p) => (p.id === id ? { ...p, label } : p))
        })
      } else if ('esp_area_id' in variables.property) {
        const espAreaId = variables.property.esp_area_id
        queryClient.setQueryData<PropertyType[]>(['properties'], (old) => {
          if (!old) return old
          const id = propertyId.value
          return old.map((property) => {
            if (property.id !== id) return property
            if (espAreaId === null) {
              return {
                ...property,
                esp_area_id: null,
                load_shedding_suburb_id: null,
                load_shedding: undefined
              }
            }
            return {
              ...property,
              esp_area_id: espAreaId
            }
          })
        })
      } else if ('battery_saver_enabled' in variables.property) {
        const { battery_saver_enabled, load_shedding_enabled } =
          variables.property
        queryClient.setQueryData<PropertyType[]>(['properties'], (old) => {
          if (!old) return old
          const id = propertyId.value
          return old.map((property) =>
            property.id === id
              ? {
                  ...property,
                  battery_saver_enabled,
                  load_shedding: property.load_shedding && {
                    ...property.load_shedding,
                    enabled: load_shedding_enabled
                  }
                }
              : property
          )
        })
      }
      queryClient.invalidateQueries({
        queryKey: ['properties']
      })
      if (onSuccessCallback) {
        onSuccessCallback(response)
      }
    },
    onError: () => {
      if (onErrorCallback) {
        onErrorCallback(mutation.error)
      }
    }
  })

  function savePropertyChanges(body: PutPayload) {
    mutation.mutate(body)
  }

  return {
    ...mutation,
    savePropertyChanges
  }
}

interface UsePropertiesPostMutationProps {
  propertyId: Ref<number>
  urlSlug: string
  onSuccessCallback?: (response: UsePropertiesPostMutationResponse) => void
  onErrorCallback?: (error: unknown) => void
}

interface UsePropertiesPostMutationResponse {
  vacation: PropertyVacationType
}

export interface PostPayload {
  vacation: AwayModeFormType
}

export const usePropertiesPostMutation = ({
  propertyId,
  urlSlug,
  onSuccessCallback,
  onErrorCallback
}: UsePropertiesPostMutationProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const mutation = useMutation<
    UsePropertiesPostMutationResponse,
    Error,
    PostPayload
  >({
    mutationFn: (body) =>
      post(
        `/properties/${propertyId.value}${urlSlug}`,
        body,
        router,
        propertyId.value.toString()
      ),
    onSuccess: (response) => {
      if (urlSlug === '/vacation') {
        queryClient.setQueryData<PropertyType[]>(['properties'], (old) => {
          if (!old) return old
          const id = propertyId.value
          return old.map((p) =>
            p.id === id ? { ...p, vacation: response.vacation } : p
          )
        })
      }
      queryClient.invalidateQueries({
        queryKey: ['properties']
      })
      if (onSuccessCallback) {
        onSuccessCallback(response)
      }
    },
    onError: () => {
      if (onErrorCallback) {
        onErrorCallback(mutation.error)
      }
    }
  })

  return mutation
}

interface UsePropertiesDelMutationProps {
  propertyId: Ref<number>
  urlSlug: string
  onSuccessCallback?: () => void
  onErrorCallback?: (error: unknown) => void
}

export const usePropertiesDelMutation = ({
  propertyId,
  urlSlug,
  onSuccessCallback,
  onErrorCallback
}: UsePropertiesDelMutationProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const mutation = useMutation<Error>({
    mutationFn: () =>
      del(
        `/properties/${propertyId.value}${urlSlug}`,
        router,
        propertyId.value.toString()
      ),
    onSuccess: () => {
      if (urlSlug === '/vacation') {
        queryClient.setQueryData<PropertyType[]>(['properties'], (old) => {
          if (!old) return old
          const id = propertyId.value
          return old.map((p) => (p.id === id ? { ...p, vacation: null } : p))
        })
      }
      queryClient.invalidateQueries({
        queryKey: ['properties']
      })
      if (onSuccessCallback) {
        onSuccessCallback()
      }
    },
    onError: () => {
      if (onErrorCallback) {
        onErrorCallback(mutation.error)
      }
    }
  })

  return mutation
}
